import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '@/hooks/useApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Download, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LabReportDetailPage() {
  const { patientId, reportId } = useParams();
  const { token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadReportData();
  }, [reportId]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const [reportData, patientData] = await Promise.all([
        api.get(`/files/lab-results/${reportId}`),
        api.get(`/patients/${patientId}`)
      ]);
      setReport(reportData);
      setPatient(patientData);
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load lab report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!report || !patient) return;
    try {
      const response = await axios.get(
        `${API_URL}/files/lab-results/${reportId}/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = `lab_report_${patient.full_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error("Failed to download PDF report. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!report) return;
    if (!confirm('Are you sure you want to delete this lab report?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/files/${patientId}/files/${report.file_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success("Lab result deleted successfully");
      navigate(`/patient/${patientId}`);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete lab result. " + (error.response?.data?.detail || "Please try again."));
    }
  };

  const handleSendEmail = async () => {
    if (!report) return;
    try {
      setSendingEmail(true);
      await axios.post(
        `${API_URL}/files/lab-results/${report.id}/email-report`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Lab report emailed successfully!");
    } catch (error) {
      console.error("Email failed:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePreviewOriginal = async () => {
    if (!report) return;
    try {
      const response = await axios.get(
        `${API_URL}/files/${patientId}/files/${report.file_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Opening original file...');
    } catch (error) {
      console.error('Failed to preview:', error);
      toast.error('Failed to preview original file');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="p-8">
          <p>Report not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/patient/${patientId}`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Header Card - Beautiful Design */}
        <Card className="rounded-2xl border-[#e5e5e5] mb-6 bg-gradient-to-br from-white to-gray-50">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              {/* Left Section - File Info */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {report.source_filename}
                    </h2>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                      {report.uploaded_by && (
                        <div className="flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Uploaded by <span className="font-medium text-gray-900">{report.uploaded_by}</span></span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{new Date(report.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section - Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={handlePreviewOriginal}
                  variant="outline"
                  size="sm"
                  className="rounded-lg hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-all"
                >
                  <Download size={16} className="mr-2" />
                  Preview Original
                </Button>
                <Button
                  onClick={handleSendEmail}
                  variant="outline"
                  size="sm"
                  className="rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all"
                  disabled={sendingEmail}
                >
                  <Mail size={16} className="mr-2" />
                  {sendingEmail ? 'Sending...' : 'Email'}
                </Button>
                <Button
                  onClick={handleDownloadPDF}
                  variant="outline"
                  size="sm"
                  className="rounded-lg hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all"
                >
                  <Download size={16} className="mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="outline"
                  size="sm"
                  className="rounded-lg text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Tabs Card - Second Card */}
        <Card className="rounded-2xl border-[#e5e5e5]">
          <CardContent className="pt-6">
            <Tabs defaultValue="results" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="results">Results</TabsTrigger>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="abnormal">Abnormal</TabsTrigger>
                <TabsTrigger value="insights">Insights</TabsTrigger>
              </TabsList>

              <TabsContent value="results" className="mt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "#e5e5e5" }}>
                        <th className="text-left py-2" style={{ color: "#333333" }}>Test</th>
                        <th className="text-right py-2" style={{ color: "#333333" }}>Value</th>
                        <th className="text-left py-2" style={{ color: "#333333" }}>Unit</th>
                        <th className="text-left py-2" style={{ color: "#333333" }}>Reference Range</th>
                        <th className="text-left py-2" style={{ color: "#333333" }}>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.lab_values && (() => {
                        const rows = [];
                        let currentPanel = null;
                        let currentSubPanel = null;
                        
                        report.lab_values.forEach((val, idx) => {
                          // Add main panel header row if panel changes
                          if (val.panel_name && val.panel_name !== currentPanel) {
                            rows.push(
                              <tr key={`panel-${idx}`} className="border-b bg-purple-100" style={{ borderColor: "#e5e5e5" }}>
                                <td colSpan="5" className="py-2 px-3 font-bold text-purple-900 text-base">
                                  {val.panel_name}
                                </td>
                              </tr>
                            );
                            currentPanel = val.panel_name;
                            currentSubPanel = null;  // Reset sub-panel when main panel changes
                          }
                          
                          // Add sub-panel header row if sub-panel changes
                          if (val.sub_panel_name && val.sub_panel_name !== currentSubPanel) {
                            rows.push(
                              <tr key={`subpanel-${idx}`} className="border-b bg-purple-50" style={{ borderColor: "#e5e5e5" }}>
                                <td colSpan="5" className="py-2 px-6 font-semibold text-purple-800 text-sm">
                                  {val.sub_panel_name}
                                </td>
                              </tr>
                            );
                            currentSubPanel = val.sub_panel_name;
                          }
                          
                          // Always prefer the full reference range text if available
                          let refRangeDisplay = "—";
                          if (val.ref_range_text && val.ref_range_text.trim()) {
                            refRangeDisplay = val.ref_range_text;
                          } else if (val.ref_low && val.ref_high) {
                            refRangeDisplay = `${val.ref_low}-${val.ref_high}`;
                          } else if (val.ref_high) {
                            refRangeDisplay = `< ${val.ref_high}`;
                          } else if (val.ref_low) {
                            refRangeDisplay = `> ${val.ref_low}`;
                          }
                          
                          // Determine indent level based on hierarchy
                          let indentStyle = "0.5rem";  // Default
                          if (val.panel_name && val.sub_panel_name) {
                            // Test under panel and sub-panel (3-level)
                            indentStyle = "3rem";
                          } else if (val.panel_name) {
                            // Test under panel only (2-level)
                            indentStyle = "2rem";
                          }
                          
                          // Display value or value_str
                          const displayValue = val.value !== null && val.value !== undefined 
                            ? val.value 
                            : (val.value_str || "N/A");
                          
                          // Add test row with appropriate indentation
                          rows.push(
                            <tr key={idx} className="border-b" style={{ borderColor: "#e5e5e5" }}>
                              <td className="py-2" style={{ 
                                color: "#333333",
                                paddingLeft: indentStyle
                              }}>
                                {val.name}
                              </td>
                              <td 
                                className="py-2 text-right font-semibold"
                                style={{ 
                                  color: val.flag === "high" ? "#e90d01" : val.flag === "low" ? "#1fb25a" : "#333333" 
                                }}
                              >
                                {displayValue}
                              </td>
                              <td className="py-2" style={{ color: "#555555" }}>{val.unit}</td>
                              <td className="py-2 text-sm" style={{ color: "#555555", maxWidth: "300px" }}>
                                {refRangeDisplay}
                              </td>
                              <td className="py-2 text-sm">
                                {val.comparison ? (
                                  <div>
                                    <div 
                                      className="font-semibold mb-1"
                                      style={{ 
                                        color: val.comparison.color === "green" ? "#1fb25a" : 
                                               val.comparison.color === "red" ? "#e90d01" : "#ff8c00"
                                      }}
                                    >
                                      {val.comparison.trend} {val.comparison.message}
                                    </div>
                                    <div className="text-xs" style={{ color: "#666666" }}>
                                      Previous: {val.comparison.previous_value} {val.unit}
                                      <br />
                                      Change: {val.comparison.change > 0 ? "+" : ""}{val.comparison.change.toFixed(2)} 
                                      ({val.comparison.change_percent}%)
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: "#999999" }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                        
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="summary" className="mt-4">
                <div className="prose max-w-none">
                  <div style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                    {report.overall_summary || "No summary available"}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="abnormal" className="mt-4">
                <div className="space-y-2">
                  {report.lab_values && report.lab_values.filter(v => v.flag === "high" || v.flag === "low").length > 0 ? (
                    report.lab_values
                      .filter(v => v.flag === "high" || v.flag === "low")
                      .map((val, idx) => (
                        <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: "#fff5f5", border: "1px solid #fee" }}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold" style={{ color: "#333333" }}>{val.name}</span>
                            <span className="text-sm font-semibold" style={{ color: val.flag === "high" ? "#e90d01" : "#1fb25a" }}>
                              {val.value} {val.unit} [{val.flag?.toUpperCase()}]
                            </span>
                          </div>
                        </div>
                      ))
                  ) : (
                    <p style={{ color: "#555555" }}>All values are within normal range</p>
                  )}
                </div>
                {report.abnormal_highlights && (
                  <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: "#fef7e0", border: "1px solid #fde68a" }}>
                    <h4 className="font-semibold mb-2" style={{ color: "#333333" }}>Clinical Notes</h4>
                    <div style={{ color: "#555555", whiteSpace: "pre-wrap" }}>
                      {report.abnormal_highlights}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="insights" className="mt-4">
                <Tabs defaultValue="english" className="w-full">
                  <TabsList>
                    <TabsTrigger value="english">English</TabsTrigger>
                    <TabsTrigger value="nepali">नेपाली (Nepali)</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="english" className="mt-4 space-y-4">
                    {report.trends_analysis && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>Trends Analysis</h4>
                        <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                          {report.trends_analysis}
                        </p>
                      </div>
                    )}
                    {report.ai_insights && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>Clinical Insights</h4>
                        <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                          {report.ai_insights}
                        </p>
                      </div>
                    )}
                    {!report.trends_analysis && !report.ai_insights && (
                      <p style={{ color: "#999999" }}>No insights available</p>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="nepali" className="mt-4 space-y-4">
                    {report.trends_analysis_nepali && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>प्रवृत्ति विश्लेषण</h4>
                        <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                          {report.trends_analysis_nepali}
                        </p>
                      </div>
                    )}
                    {report.ai_insights_nepali && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>चिकित्सा सल्लाह</h4>
                        <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                          {report.ai_insights_nepali}
                        </p>
                      </div>
                    )}
                    {!report.trends_analysis_nepali && !report.ai_insights_nepali && (
                      <p style={{ color: "#999999" }}>थप जानकारी उपलब्ध छैन।</p>
                    )}
                  </TabsContent>
                </Tabs>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
